/**
 * Whether the DM's suggested actions are folded away — one boolean, and the only reason it is a
 * store is that it has nowhere else to live.
 *
 * **Why the shelf exists at all.** The action bar is clamped to `--measure`, the *narration*
 * reading measure, which is 34rem of a screen that is often three times that. Six suggestions in
 * a language that writes long — Ukrainian and German both do — therefore wrap to two lines each,
 * and the foot ends up about half the viewport with the story squeezed into the rest. Widening
 * the bar or laying the cards out two-across were the other two answers; both take the decision
 * away from the player, and this one hands it to them. A reader who is reading gets their screen
 * back, and a player who writes their own actions can keep it that way permanently.
 *
 * **Why it is not `useState` in the component.** `PlayRoute` renders no action bar at all while a
 * turn is live — the controls are absent rather than disabled, and `ActionBar`'s header argues
 * why — so the component unmounts for the whole of every turn. Component state would un-collapse
 * itself once per chapter, which is the one behaviour a preference must not have.
 *
 * **Why it is not in `reading.ts`.** That store's own docstring makes its set of fields one list,
 * mapped by `readingVariables` onto three CSS custom properties, precisely so a reset cannot leave
 * one behind. A boolean that maps to no property would break that argument for the sake of sharing
 * a `localStorage` key. `draft.ts` is out for the mirror-image reason: it is documented as
 * deliberately the smallest store that makes the suggestion loop work.
 *
 * So: its own file, and the storage handled the way `reading.ts` handles it — read once at store
 * creation, written in the setter, both directions inside a `try` for the private-mode browser
 * that throws on `localStorage` rather than returning null.
 */
import { create } from 'zustand';

const STORAGE_KEY = 'wasteland.suggestions';

interface SuggestionsStore {
  /** True when the cards are folded away and only the action box is drawn. */
  collapsed: boolean;
  toggle: () => void;
  /** Fold the cards back out. Called by the number keys, which must not be a dead end. */
  expand: () => void;
}

export const useSuggestionsStore = create<SuggestionsStore>()((set) => ({
  collapsed: readCollapsed(),

  toggle: () =>
    set((state) => {
      const collapsed = !state.collapsed;
      writeCollapsed(collapsed);
      return { collapsed };
    }),

  expand: () =>
    set((state) => {
      if (!state.collapsed) return state;
      writeCollapsed(false);
      return { collapsed: false };
    }),
}));

/**
 * Expanded unless the disk says otherwise, and expanded for anything the disk says that is not
 * the literal `true`. The default has to *be* the design — the suggestions are the game's main
 * affordance and a player who has never touched the toggle must see them.
 */
export function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  } catch {
    // The setting still applies to this session; refusing to fold a list because a browser
    // will not remember it is not a trade worth making.
  }
}
